<template>
    <div class="filelayout-edit-vue org_settings mb-5">
        <h2 class="mb-2">File Layout Editor</h2>
        <div class="row">
            <div class="col">
                <div class="card mb-3 table-card">
                    <div class="row py-3 px-2">
                        <div class="col">
                            <label class="form-label">File Layout Name</label>
                            <input type="text" class="form-control" v-model="current_layout.name" :class="{ 'is-invalid': errors.file_layout_name }" @change="errors.file_layout_name = ''" placeholder="Enter a name for this file layout" :disabled="!canEdit('direct_mail')" aria-label="File Layout Name">
                            <div class="invalid-feedback" v-if="errors.file_layout_name">
                                Please enter a file layout name.
                            </div>
                        </div>
                    </div>

                    <div class="card-header">
                        <div class="row">
                            <div class="col-lg-6 col-12">
                                <h3 class="mt-2">Export Columns ({{filtered_columns.length}})</h3>
                            </div>
                            <div class="col-lg-6 d-none d-lg-block">
                                <div class="d-flex justify-content-end justify-self-end gap-2">
                                    <div class="position-relative">
                                        <input 
                                            type="text" 
                                            class="search-box form-control py-2 me-1" 
                                            placeholder="Search by Column Name..." 
                                            aria-label="Search by Column Name..." 
                                            v-model="search_key" 
                                            @input="handleSearch()"
                                        >
                                        <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                                    </div>
                                    <div v-if="!canEdit('direct_mail')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary" :disabled="true">
                                            <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add Column
                                        </button>
                                    </div>
                                    <button v-else class="btn btn-primary" @click="openColumnModal('add')" :disabled="current_layout.id === -1">
                                        <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add Column
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card-body p-0">
                        <table class="table table-responsive table-with-pagination m-0">
                            <thead>
                                <tr class="table-header-row">
                                    <th @click="sortByKey('export_column_name')" class="table-column-1">
                                        <div class="d-flex flex-row">
                                            Export Column Name
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(sort_key === 'export_column_name' && sort_order === 'asc') ? 'sort-key-active' : ''" alt="">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(sort_key === 'export_column_name' && sort_order === 'des') ? 'sort-key-active' : ''" alt="">
                                            </div>
                                        </div>
                                    </th>
                                    <th @click="sortByKey('column')" class="table-column-3">
                                        <div class="d-flex flex-row">
                                            Source Column
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(sort_key === 'column' && sort_order === 'asc') ? 'sort-key-active' : ''" alt="">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(sort_key === 'column' && sort_order === 'des') ? 'sort-key-active' : ''" alt="">
                                            </div>
                                        </div>
                                    </th>
                                    <th class="table-column-2">Modification</th>
                                    <th class="table-column-4">Default Value</th>
                                    <th class="table-column-2 pe-3" width="1" v-if="canEdit('direct_mail')">Actions</th>
                                </tr>
                            </thead>
                            <draggable v-model="list_shown" tag="tbody" @end="updateColumnPositions" item-key="id" :disabled="!canEdit('direct_mail')">
                                <template #item="{ element: col, index }">
                                    <tr :class="`table-row-${index+1}`" class="draggable">
                                        <td class="align-middle table-column-1">
                                            <div class="handle d-inline-block me-2" v-if="canEdit('direct_mail')">
                                                <img src="/img/icons/avid-icons/drag-n-drop.svg" alt="drag" class="icon">
                                            </div>
                                            {{ col.export_column_name }}</td>
                                        <td class="align-middle table-column-2">{{ formatColumn(col.column) }}</td>
                                        <td class="align-middle table-column-3">
                                            <template v-if="col.modifications !== undefined && (col.modifications.length > 1 || (col.modifications.length === 1 && col.modifications[0].action !== 'no_change'))">
                                                <span v-html="expandedModifications[col.id]
                                                            ? getModificationsSummary(col.modifications)
                                                            : getModificationsSummary(col.modifications).slice(0, 100) + (getModificationsSummary(col.modifications).length > 100 ? '... ' : '')">
                                                </span>
                                                <a v-if="getModificationsSummary(col.modifications).length > 100" href="#" @click.prevent="toggleModificationsExpand(col.id)" class="ms-2">
                                                    {{ expandedModifications[col.id] ? '' : 'Show more' }}
                                                </a>
                                            </template>
                                        </td>
                                        <td class="align-middle table-column-4">{{ col.default_value }}</td>
                                        <td class="align-middle table-column-5" v-if="canEdit('direct_mail')">
                                            <div class="dropdown d-flex justify-content-center">
                                                <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false">
                                                    <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                                                </button>
                                                <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                                    <a class="dropdown-item" href="#" @click.prevent="copyColumn(col)"
                                                       :class="{ 'disabled': current_layout.id < 0 }"
                                                       :title="(current_layout.id < 0) ? 'Name the file layout in the field above before copying columns' : ''">
                                                        <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy Column
                                                    </a>
                                                    <a class="dropdown-item" href="#" @click.prevent="editColumn(col)"
                                                       :class="{ 'disabled': current_layout.id < 0 }"
                                                       :title="(current_layout.id < 0) ? 'Name the file layout in the field above before editing columns' : ''">
                                                        <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit Column
                                                    </a>
                                                    <hr class="my-1">
                                                    <a class="dropdown-item text-danger" href="#" @click.prevent="removeColumn(col)"
                                                       :class="{ 'disabled': current_layout.id === -1 }"
                                                       :title="(current_layout.id === -1) ? 'Name the file layout in the field above before editing columns' : ''">
                                                        <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Column
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </draggable>
                            <tr v-if="!list_shown.length">
                                <td colspan="5" class="text-center p-3">You have no columns defined. <a href="#" @click.prevent="openColumnModal('add')" class="bg-white" v-if="canEdit('direct_mail')">Add one here.</a><span v-else>No columns defined.</span></td>
                            </tr>
                            <tfoot class="table-border-bottom-0">
                                <tr>
                                    <td colspan="100%" class="py-0">
                                        <div class="table-pagination">
                                            <div class="table-pagination--group d-none d-md-flex">
                                                <div class="position-relative">
                                                    <select 
                                                        class="form-select" 
                                                        name="num_per_page" 
                                                        id="num_per_page" 
                                                        v-model="num_per_page" 
                                                        @change="reloadPagination(filtered_columns);"
                                                     aria-label="Num Per Page">
                                                        <option value="5000">ALL</option>
                                                        <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                                    </select>
                                                    <div class="select-display-text">
                                                        Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="table-pagination--group d-none d-lg-flex">
                                                {{ filtered_columns.length === 0 ? '0' : item_interval }} of {{ filtered_columns.length }}
                                            </div>

                                            <div class="table-pagination--group">
                                                <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage(filtered_columns)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                                    <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                                                </button>

                                                <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                                    <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page === page_index + 1 ? 'current-page' : ''" @click="moveToPage(page, filtered_columns)">
                                                        {{ page }}
                                                    </button>
                                                </div>
                                                <div class="d-flex page-selector" v-else>
                                                    <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page === page_index + 1 ? 'current-page' : ''" @click="moveToPage(page, filtered_columns)">
                                                        {{ page }}
                                                    </button>

                                                    <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                                    ...
                                                    </button>
                                                    <button v-if="page_index >= 2 && page_index < page_index_array.length - 1" class="btn btn-none btn-icon-only-md current-page">
                                                        {{ page_index + 1 }}
                                                    </button>

                                                    <button v-if="page_index < page_index_array.length - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                                    ...
                                                    </button>
                                                    <button class="btn btn-none btn-icon-only-md" :class="page_index === page_index_array.length - 1 ? 'current-page' : ''" @click="moveToPage(page_index_array.length, filtered_columns)" :key="page_index_array.length">
                                                        {{ page_index_array.length }}
                                                    </button>
                                                </div>

                                                <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage(filtered_columns)" :disabled="page_index === page_count - 1" data-bs-toggle="tooltip" title="Next page">
                                                    <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
            <div class="invalid-feedback mb-3" v-if="errors.columns">
                Please add at least one column.
            </div>
        </div>
        <div class="row">
            <div class="col">
                <div v-if="!canEdit('direct_mail')" class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                    <button class="btn btn-primary" :disabled="true">
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                    </button>
                </div>
                <button v-else class="btn btn-primary" :disabled="form.saving" @click="saveLayout()">
                    <span v-if="form.saving">
                        <div class="spinner-border spinner-border-sm float-left" role="status">
                            <span class="visually-hidden">Saving...</span>
                        </div>
                        Saving
                    </span>
                    <span v-else>
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                    </span>
                </button>
                <button class="btn btn-secondary ms-2" @click="back()">
                    <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                </button>
            </div>
        </div>

        <!-- Column Edit Modal -->
        <div class="modal fade" id="column-edit-modal" tabindex="-1" aria-labelledby="column-edit-modal-label" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <div class="text-center w-100">
                            <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                            <h3 class="mt-2">{{ current_column.id >= 0 ? 'Edit' : 'Add' }} Export Column</h3>
                        </div>
                    </div>
                    <div class="modal-body pb-0">
                        <div class="row">
                            <div class="col">
                                <div class="form-group mb-3" :class="{ 'is-invalid': errors.source }">
                                    <label class="form-label">Source of Data</label>
                                    <div class="input-group">
                                        <button class="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" :class="{ 'is-invalid': errors.source }">
                                            {{ getValueTypeDisplay(current_column.value_type) }}
                                        </button>
                                        <ul class="dropdown-menu dropdown-menu-primary dropdown-menu-start">
                                            <li><a class="dropdown-item" @click.prevent="handleValueTypeChange('column')">Column Value</a></li>
                                            <li><a class="dropdown-item" @click.prevent="handleValueTypeChange('static')">Static Value</a></li>
                                        </ul>

                                        <accordion-select v-if="current_column.value_type == 'column'" v-model="current_column.column" :options="allColumns" @update:modelValue="changeColumn()" :class="{ 'is-invalid': errors.source }"></accordion-select>

                                        <input v-else-if="current_column.value_type == 'static'" type="text" class="form-control" placeholder="Enter static value" v-model="current_column.column" @change="errors.source = false" :class="{ 'is-invalid': errors.source }" aria-label="Enter static value">
                                    </div>
                                    <div class="invalid-feedback" v-if="errors.source">
                                        {{ errors.source }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col">
                                <div class="form-group mb-3" :class="{ 'is-invalid': errors.name }">
                                    <label class="form-label">Export Column Name</label>
                                    <input type="text" class="form-control" v-model="current_column.export_column_name"
                                        :class="{ 'is-invalid': errors.name }" @input="validateExportColumnName()"  aria-label="Export Column Name">
                                    <div class="invalid-feedback" v-if="errors.name">
                                        {{ errors.name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row" v-if="current_column.value_type != 'static' && !columnIsNumber()">
                            <div class="col">
                                <div class="form-group mb-3">
                                    <label class="form-label">Default Value <em class="text-muted">(optional)</em></label>
                                    <input type="text" class="form-control" v-model="current_column.default_value" aria-label="Default Value">
                                </div>
                            </div>
                        </div>

                        <!-- Existing modifications table for numbers -->
                        <div v-if="columnIsNumber()" class="">
                            <label class="form-label">Modifications</label>
                            <div class="card table-card mb-2">
                                <table class="table table-responsive table-without-pagination m-0">
                                    <thead>
                                        <tr class="table-header-row">
                                            <th scope="col" class="py-3" style="width:150px;">From <small><em>(Inclusive)</em></small></th>
                                            <th scope="col" class="py-3"  style="width:150px;">To <small><em>(Inclusive)</em></small></th>
                                            <th scope="col" class="py-3" >Action</th>
                                            <th scope="col" class="py-3"  style="width:1%;white-space:nowrap;"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(mod, index) in current_column.modifications" :key="index" :class="{ 'last-row': index === current_column.modifications.length - 1 }">
                                            <td>
                                                <div class="input-group">
                                                    <span class="input-group-text">$</span>
                                                    <input type="number" class="form-control" disabled="true" v-model="mod.from" aria-label="From">
                                                </div>
                                            </td>
                                            <td>
                                                <div class="input-group">
                                                    <span class="input-group-text">$</span>
                                                    <input type="number" class="form-control" placeholder="∞" v-model="mod.to"
                                                        @change="changedModificationTo(index)" aria-label="∞">
                                                </div>
                                            </td>
                                            <td>
                                                <div class="input-group mb-1">
                                                    <button class="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" v-html="getModificationType(mod.action)"></button>
                                                    <div class="dropdown-menu dropdown-menu-primary">
                                                        <a class="dropdown-item" href="#" @click.prevent="mod.action = 'no_change';">No Changes to Number</a>
                                                        <a class="dropdown-item" href="#" @click.prevent="mod.action = 'multiply';">Multiply the Number</a>
                                                        <a class="dropdown-item" href="#" @click.prevent="mod.action = 'override';">Override the Number</a>
                                                    </div>
                                                    <input type="number" class="form-control" v-model="mod.value"
                                                        :placeholder="getModificationPlaceholder(mod)"
                                                        v-if="mod.action !== 'no_change'" aria-label="getModificationPlaceholder(mod)">
                                                </div>
                                                <div class="row">
                                                    <div class="col">
                                                        <div class="form-check" v-if="mod.action !== 'no_change'">
                                                            <label class="form-check-label">
                                                                <input class="form-check-input" type="checkbox" value="true" v-model="mod.format_num">
                                                                <small>Format output</small>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="col">
                                                        <div class="form-check d-flex align-items-center mb-0" v-if="mod.action !== 'no_change'">
                                                            <label class="form-check-label  gap-2 mb-0" style="white-space: nowrap;">
                                                                <input class="form-check-input me-1" type="checkbox" value="true" v-model="mod.should_round" aria-label="Should Round">
                                                                <small>
                                                                    Round to nearest
                                                                    <div class="btn-group ms-1" style="vertical-align: middle;">
                                                                        <button class="btn dropdown-toggle btn-sm px-1 py-0" type="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" style="white-space: nowrap;">
                                                                            ${{mod.round_to_nearest }}
                                                                        </button>
                                                                        <div class="dropdown-menu dropdown-menu-primary">
                                                                            <a class="dropdown-item" href="#" @click.prevent="mod.round_to_nearest = 1">$1</a>
                                                                            <a class="dropdown-item" href="#" @click.prevent="mod.round_to_nearest = 5">$5</a>
                                                                            <a class="dropdown-item" href="#" @click.prevent="mod.round_to_nearest = 10">$10</a>
                                                                            <a class="dropdown-item" href="#" @click.prevent="mod.round_to_nearest = 25">$25</a>
                                                                            <a class="dropdown-item" href="#" @click.prevent="mod.round_to_nearest = 50">$50</a>
                                                                            <a class="dropdown-item" href="#" @click.prevent="mod.round_to_nearest = 100">$100</a>
                                                                        </div>
                                                                    </div>
                                                                </small>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div v-if="!canEdit('direct_mail')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                                    <button class="btn btn-danger" :disabled="true" v-if="index >= 1">
                                                        <img class="icon" src="/img/icons/avid-icons/trash.svg" data-bs-toggle="tooltip"
                                                            title="Delete File Layout" alt="">
                                                    </button>
                                                </div>
                                                <button v-else class="btn btn-none" @click="removeModificationRow(index)" v-if="index >= 1">
                                                    <img class="icon" src="/img/icons/avid-icons/trash.svg" data-bs-toggle="tooltip"
                                                        title="Delete File Layout" alt="">
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="text-danger invalid-feedback mt-2" v-if="errors.value">
                                {{ errors.value }}
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-primary" @click="applyColumnChanges()" :disabled="current_layout.id === -1">
                            <i class="fa fa-check"></i> Apply
                        </button>
                        <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import draggable from 'vuedraggable';
import tablePaginationMixin from '../../mixins/table_pagination';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';

import permissionsMixin from '../../mixins/permissions'

export default defineComponent({
    name: 'FileLayoutEdit',
    props: {
        client: {
            type: Object,
            required: true
        },
        layout: {
            type: Object,
            required: true
        },
        data_blend: {
            type: Object,
            required: true
        }
    },
    components: {
        draggable
    },
    mixins: [tablePaginationMixin, permissionsMixin],
    data() {
        return {
            form: {
                saving: false,
                error: false
            },
            errors: {
                file_layout_name: '',
                name: '',
                source: '',
                value: '',
                columns: ''
            },
            current_layout: null,
            current_column: {
                id: -1,
                file_layout_id: -1,
                source: "appeal",
                value_type: "column",
                column: "",
                is_numeric: false,
                modifications: [
                    {
                        from: 0,
                        to: null,
                        action: "no_change",
                        value: "",
                        format_num: false,
                        round_to_nearest: "1",
                        should_round: false
                    }
                ],
                export_column_name: "",
                default_value: "",
                order_num: 0
            },
            db_columns: [],
            list_shown: [],
            filtered_columns: [],
            search_key: '',
            sort_key: 'export_column_name',
            sort_order: 'asc',
            columnModal: null,
            expandedModifications: {},
        };
    },
    beforeMount() {
        if(this.data_blend) {
            this.loadDatabaseColumns();
        } else {
            this.db_columns = [];
        }
        if (this.layout) {
            //make a copy of the layout
            this.current_layout = JSON.parse(JSON.stringify(this.layout));
            this.current_layout.pending_changes = false;
            this.current_column.file_layout_id = this.layout.id;

            if(this.current_layout.columns == null) {
                this.current_layout.columns = [];
            }

            if(this.current_layout.columns && this.current_layout.columns.length > 0)
                this.filtered_columns = [...this.current_layout.columns];
            else
                this.filtered_columns = [];
            this.reloadPagination(this.filtered_columns);
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        }
    },
    mounted() {
        this.columnModal = new window.bootstrap.Modal(document.getElementById('column-edit-modal'));

        this.initializeTooltips();
    },
    computed: {
        allColumns() {
            var array_to_return = [];
            
            // Add appeal columns
            const appealColumns = [
                {text: "Appeal Code", value: "appeal_code", type:"string", category: "Direct Mail Settings"},
                {text: "Appeal Name", value: "appeal_name", type:"string", category: "Direct Mail Settings"},
                {text: "Carrier Postage", value: "carrier_postage", type:"string", category: "Direct Mail Settings"},
                {text: "Carrier Size", value: "carrier_size", type:"string", category: "Direct Mail Settings"},
                {text: "Double Sided", value: "double_sided", type:"string", category: "Direct Mail Settings"},
                {text: "Drop Date", value: "drop_date", type:"date", category: "Direct Mail Settings"},
                {text: "Experiment Version", value: "CASE WHEN appeal.experiment = 1 THEN random_value.experiment_id ELSE NULL END", type:"string", category: "Direct Mail Settings"},
                {text: "Finder Number", value: "finder_number", type:"number", category: "Direct Mail Settings"},
                {text: "Letter Version", value: "letter_version", type:"string", category: "Direct Mail Settings"},
                {text: "Package Code", value: "package_code", type:"string", category: "Direct Mail Settings"},
                {text: "Package Description", value: "package_description", type:"string", category: "Direct Mail Settings"},
                {text: "Reply Card Version", value: "reply_card_version", type:"string", category: "Direct Mail Settings"},
                {text: "Reply Postage", value: "reply_postage", type:"string", category: "Direct Mail Settings"},
                {text: "Segment Code", value: "segment_code", type:"string", category: "Direct Mail Settings"},
                // {text: "X-Axis Segment", value: "x_axis", type:"string"},
                // {text: "Y-Axis Segment", value: "y_axis", type:"string"},
                // {text: "Z-Axis Segment", value: "z_axis", type:"string"}
            ];
            array_to_return.push(...appealColumns);

            // Add contact columns
            for(var i = 0; i < this.db_columns.length; i++) {
                if((this.db_columns[i].value.indexOf("contact") === 0  ||
                    this.db_columns[i].value == 'id' ||
                    this.db_columns[i].value == 'account_type') ||
                    this.db_columns[i].value.indexOf("custom_calculations.") === 0
                ) {
                    
                    let columnText = this.db_columns[i].text;
                    if(this.db_columns[i].text.indexOf("Custom ") === 0) {
                        columnText = this.db_columns[i].text.replace("Custom ", "");
                    }
                    else if(this.db_columns[i].value == 'id') {
                        columnText = 'Account ID';
                    }
                    array_to_return.push({
                        text: columnText,
                        value: this.db_columns[i].value,
                        type: this.db_columns[i].type
                    });
                }
            }

            // Add giving columns
            for(var i = 0; i < this.db_columns.length; i++) {
                if((this.db_columns[i].value.indexOf("giving") === 0) && (
                    this.db_columns[i].value.indexOf("amount") != -1 ||
                    this.db_columns[i].value.indexOf("ask") != -1 ||
                    this.db_columns[i].value.indexOf("date") != -1 ||
                    this.db_columns[i].value.split(".").length - 1 == 1
                  ) && this.db_columns[i].value.indexOf("range") == -1
                ) {
                    
                    array_to_return.push(this.db_columns[i]);
                }
            }

            //Sort the array by text
            array_to_return.sort((a, b) => a.text.localeCompare(b.text));
            return array_to_return;
        },
        columns() {
            // Keep the old computed property for backward compatibility
            return this.allColumns;
        }
    },
    methods: {
        formatColumn(column) {
            if (column === null || column === undefined || column === '') {
                return '';
            }

            if (typeof column !== 'string') {
                return String(column);
            }

            if(column.indexOf('CASE WHEN') != -1) {
                return 'Experiment Version';
            }
            //If there is a . then replace the first on with a : and all subsequent . with a space
            let array = column.split(".");
            if(array.length > 1) {
                array[0] = array[0] + ":";
            }

            for(var i = 0; i < array.length; i++) {
                array[i] = array[i].replace("_", " ");
                //Also propercase all of the words
                array[i] = array[i].split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
            }

            let join = array.join(" ");
            let arr = join.split(" ").filter((word, index, arr) => {
                return arr.indexOf(word) === index;
            });

            return arr.join(" ");
        },
        loadDatabaseColumns() {
            let data = {
                client: this.client,
                include_trans: false,
                source: 'data_blend',
                data_source_id: this.data_blend.id,
            };
            window.axios.post('/api/bigquery/get_db_columns', data)
            .then(response => {
                this.db_columns = response.data.columns;
            });
        },
        setDefaultColumn() {
            this.current_column = {
                id: -1 * Math.round(Math.random()*10000),
                file_layout_id: this.current_layout.id,
                source: "appeal",
                value_type: "column",
                column: "",
                is_numeric: false,
                modifications: [
                    {
                        from: 0,
                        to: null,
                        action: "no_change",
                        value: "",
                        format_num: false,
                        round_to_nearest: "1",
                        should_round: false
                    }
                ],
                export_column_name: "",
                default_value: "",
                order_num: 0,
            };
        },
        getValueTypeDisplay(valueType) {
            if(valueType == "column")
                return 'Column Value';
            else if(valueType == "static")
                return "Static Value";
            return 'Column Value';
        },

        columnIsNumber() {
            var num_types = ['int', 'integer', 'float', 'money', 'number', 'double'];
            //Look through the database columns
            for(var i = 0; i < this.db_columns.length; i++) {
                //When I find the column, see if it is a number
                if(this.current_column.column == this.db_columns[i].value || 
                typeof this.current_column.column == 'object' && this.current_column.column.value == this.db_columns[i].value)
                    return num_types.includes(this.db_columns[i].type);
            }
            return false;
        },
        validateForm() {
            let isValid = true;
            this.errors = {
                file_layout_name: '',
                name: '',
                source: '',
                value: ''
            };

            if (!this.current_layout.name) {
                this.errors.file_layout_name = 'Please enter a file layout name.';
                isValid = false;
            }
            if(this.current_layout.columns.length == 0) {
                this.errors.columns = 'Please add at least one column.';
                isValid = false;
            }

            return isValid;
        },
        validateExportColumnName() {
            // Clear the error first
            this.errors.name = '';
            
            // If the field is empty, don't show an error (let validateColumnForm handle that)
            if (!this.current_column.export_column_name || this.current_column.export_column_name.trim() === '') {
                return;
            }
            
            // Check if the column name is already in use by another column
            const duplicateColumn = this.current_layout.columns.find(col =>
                col.export_column_name && 
                col.export_column_name.trim().toLowerCase() === this.current_column.export_column_name.trim().toLowerCase() &&
                col.id !== this.current_column.id
            );

            if (duplicateColumn) {
                this.errors.name = 'This column name is already in use. Please choose a different name.';
            }
        },
        validateColumnForm() {
            let isValid = true;
            this.errors = {
                name: '',
                source: '',
                value: ''
            };

            if (!this.current_column.export_column_name) {
                this.errors.name = 'Please enter a column name to use in the export.';
                isValid = false;
            } else {
                // Check if the column name is already in use by another column
                const duplicateColumn = this.current_layout.columns.find(col =>
                    col.export_column_name && 
                    col.export_column_name.trim().toLowerCase() === this.current_column.export_column_name.trim().toLowerCase() &&
                    col.id !== this.current_column.id
                );

                if (duplicateColumn) {
                    this.errors.name = 'This column name is already in use. Please choose a different name.';
                    isValid = false;
                }
            }

            if (this.current_column.value_type === 'column' && !this.current_column.column) {
                this.errors.source = 'Please select a source column.';
                isValid = false;
            } else if (this.current_column.value_type === 'static' && !this.current_column.column) {
                this.errors.source = 'Please enter a static value.';
                isValid = false;
            }

            if (this.columnIsNumber() && this.current_column.modifications) {
                for (let mod of this.current_column.modifications) {
                    if (mod.action !== 'no_change' && !mod.value) {
                        this.errors.value = 'Please enter values for all modifications.';
                        isValid = false;
                        break;
                    }
                }
                // Check for overlapping ranges
                const mods = this.current_column.modifications;
                for (let i = 0; i < mods.length; i++) {
                    let fromA = parseFloat(mods[i].from);
                    let toA = (mods[i].to === null || mods[i].to === '' || mods[i].to === undefined) ? Infinity : parseFloat(mods[i].to);
                    for (let j = i + 1; j < mods.length; j++) {
                        let fromB = parseFloat(mods[j].from);
                        let toB = (mods[j].to === null || mods[j].to === '' || mods[j].to === undefined) ? Infinity : parseFloat(mods[j].to);
                        // Only flag as overlap if the ranges actually overlap, not just touch
                        if (fromA < toB && fromB < toA) {
                            this.errors.value = 'Modification ranges cannot overlap.';
                            isValid = false;
                            break;
                        }
                    }
                    if (!isValid) break;
                }
            }

            return isValid;
        },
        openColumnModal(mode) {
            if (mode === 'add') {
                this.setDefaultColumn();
            }
            this.columnModal.show();
        },
        closeColumnModal() {
            this.columnModal.hide();
            this.setDefaultColumn();
            this.errors = {
                name: '',
                source: '',
                value: ''
            };
        },
        applyColumnChanges() {
            if (!this.validateColumnForm()) {
                return;
            }
            const existingColumn = this.current_layout.columns.find(col => col.id === this.current_column.id);
                
            if (this.current_column.id <= 0 && !existingColumn) {
                // Check if column with same ID already exists
                this.current_column.file_layout_id = this.current_layout.id;
                this.current_column.order_num = this.current_layout.columns.length;
                this.current_column.column = typeof this.current_column.column === 'object' ? this.current_column.column.value : this.current_column.column;
                this.current_layout.columns.push({ ...this.current_column });
            } else {
                const index = this.current_layout.columns.findIndex(col => col.id === this.current_column.id);
                if (index !== -1) {
                    this.current_layout.columns.splice(index, 1, { ...this.current_column });
                }
            }
            this.errors.columns = '';

            this.current_layout.pending_changes = true;
            this.filtered_columns = [...this.current_layout.columns];
            this.reloadPagination(this.filtered_columns);
            this.$nextTick(() => {
                this.initializeTooltips();
            });
            this.closeColumnModal();
        },
        saveLayout() {
            if (!this.validateForm()) {
                return;
            }

            this.form.saving = true;

            //Loop through the columns and if the value_type is 'static' change the source to be 'static'
            this.current_layout.columns.forEach(col => {
                if (col.value_type === 'static') {
                    col.source = 'static';
                }
            });

            const request = this.current_layout.id == undefined || this.current_layout.id === -1 ?
                window.axios.post('/api/file_layout', this.current_layout) :
                window.axios.put(`/api/file_layout/${this.current_layout.id}`, this.current_layout);

            request.then(response => {
                this.form.saving = false;
                Swal.fire({
                    title: "File Layout Saved Successfully!",
                    icon: "success",
                    customClass: {
                        popup: 'swal-success'
                    },
                }).then((result) => {
                    this.finishedLayout();
                });
            }).catch(error => {
                this.form.saving = false;
                this.showNotification({
                    text: 'Error saving file layout. Please try again.',
                    type: 'error'
                });
            });
        },
        // Remove a column based on its unique id rather than the visible index to avoid
        // mismatches caused by sorting, filtering, or pagination.
        removeColumn(column) {
            const idx = this.current_layout.columns.findIndex(c => c.id === column.id);
            if (idx === -1) {
                return; // Column not found – nothing to do.
            }
            this.current_layout.columns.splice(idx, 1);
            // Update order numbers
            this.current_layout.columns.forEach((col, i) => {
                col.order_num = i;
            });
            this.current_layout.pending_changes = true;
            this.filtered_columns = [...this.current_layout.columns];
            this.reloadPagination(this.filtered_columns);
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        updateColumnPositions(event) {
            // Get the old and new indices from the drag event
            const { oldIndex, newIndex } = event;
            
            // Validate indices
            if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) {
                return;
            }
            
            // Calculate the global indices in the full filtered_columns array
            const pageStartIndex = this.page_index * this.num_per_page;
            const oldGlobalIndex = pageStartIndex + oldIndex;
            const newGlobalIndex = pageStartIndex + newIndex;
            
            // Validate global indices
            if (oldGlobalIndex < 0 || oldGlobalIndex >= this.filtered_columns.length || 
                newGlobalIndex < 0 || newGlobalIndex >= this.filtered_columns.length) {
                console.warn('Invalid drag indices:', { oldIndex, newIndex, oldGlobalIndex, newGlobalIndex, totalColumns: this.filtered_columns.length });
                return;
            }
            
            // Get the column that was moved
            const movedColumn = this.filtered_columns[oldGlobalIndex];
            if (!movedColumn) {
                console.warn('Column not found at index:', oldGlobalIndex);
                return;
            }
            
            // Remove the column from its original position
            this.filtered_columns.splice(oldGlobalIndex, 1);
            
            // Insert it at the new position
            this.filtered_columns.splice(newGlobalIndex, 0, movedColumn);
            
            // Update order numbers for all columns
            this.filtered_columns.forEach((col, i) => {
                col.order_num = i;
            });
            
            // Update the main columns array
            this.current_layout.columns = [...this.filtered_columns];
            this.current_layout.pending_changes = true;
            
            // Store current page index
            const currentPageIndex = this.page_index;
            
            // Reload pagination to reflect the changes
            this.reloadPagination(this.filtered_columns);
            
            // Restore the current page index
            this.page_index = currentPageIndex;
            this.updateItemInterval(this.filtered_columns);
            
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        editColumn(col) {
            if (typeof col.modifications === 'string' || col.modifications instanceof String) {
                col.modifications = JSON.parse(col.modifications);
            }
            this.current_column = { ...col };
            
            // Handle backward compatibility for existing columns without value_type
            if (!this.current_column.value_type) {
                if (this.current_column.source === 'static') {
                    this.current_column.value_type = 'static';
                } else {
                    this.current_column.value_type = 'column';
                }
            }
            
            this.openColumnModal('edit');
        },
        addModificationRow: function() {
            var from = 0;
            if(this.current_column.modifications.length > 0) {
                from = parseFloat(this.current_column.modifications[this.current_column.modifications.length-1].to)+0.01;
            }

            this.current_column.modifications.push(
                {
                    from: from,
                    to: null,
                    action: "no_change",
                    value: "",
                    format_num: false,
                    round_to_nearest: "1",
                    should_round: false
                }
            );
        },
        removeModificationRow: function(index) {
            this.current_column.modifications.splice(index, 1);

            //If it wasn't the last one...
            if(this.current_column.modifications.length > index)
                //Change the "from" variable to reflect the next step in the previous row
                this.current_column.modifications[index].from = parseFloat(this.current_column.modifications[index-1].to) + 0.01;
            else
                //Otherwise, remove the end number to revert it back to infinity
                this.current_column.modifications[index-1].to = null
        },
        getModificationType:function(action) {
            if(action == 'no_change')
                return 'No Changes to Number';
            else if(action == 'multiply')
                return 'Multiply the Number';
            return 'Override the Number';
        },
        getModificationPlaceholder:function(mod) {
            if(mod.action == 'no_change')
                return '';
            else if(mod.action == 'multiply')
                return 'Multiply by...';
            return 'Override with...';
        },
        changedModificationTo:function(index){
            var to = this.current_column.modifications[index].to;
            //If the "to" value isn't empty and it is the last row, add a new row
            if(to != "" && index == this.current_column.modifications.length-1) {
                this.addModificationRow();
            }
            //If it is in the middle, change the next row's "from" variable
            else if(to != "" && index < this.current_column.modifications.length-1) {
                this.current_column.modifications[index+1].from = parseFloat(this.current_column.modifications[index].to) + 0.01;
            }
        },
        finishedLayout() {
            window.location = `/direct_mail/${this.client.url}/file_layouts`;
        },
        handleSearch() {
            this.filtered_columns = this.current_layout.columns.filter(col =>
                String(col.export_column_name || '').toLowerCase().includes(this.search_key.toLowerCase()) ||
                String(col.column || '').toLowerCase().includes(this.search_key.toLowerCase())
            );
            this.page_index = 0;
            this.reloadPagination(this.filtered_columns);
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }

            this.filtered_columns.sort((a, b) => {
                if (this.sort_order === 'asc') {
                    return (a[key] > b[key]) ? 1 : -1;
                } else {
                    return (a[key] < b[key]) ? 1 : -1;
                }
            });

            this.reloadPagination(this.filtered_columns);
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        back() {
            if (this.current_layout.pending_changes) {
                Swal.fire({
                    title: 'Unsaved Changes',
                    text: 'You have unsaved changes. Do you want to save before leaving?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Save',
                    cancelButtonText: 'Discard',
                    showDenyButton: true,
                    denyButtonText: 'Stay',
                    customClass: {
                        popup: 'swal-warning'
                    },
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.saveLayout();
                    } else if (result.isDismissed) {
                        this.finishedLayout();
                    }
                });
            } else {
                this.finishedLayout();
            }
        },
        copyColumn(col) {
            // Create a deep copy of the column
            const copiedColumn = JSON.parse(JSON.stringify(col));

            // Reset the id and update the name to indicate it's a copy
            copiedColumn.id = -1 * Math.round(Math.random()*10000);
            copiedColumn.export_column_name = `${copiedColumn.export_column_name} (Copy)`;

            // Find the index of the original column
            const originalIndex = this.current_layout.columns.findIndex(c => c.id === col.id);

            // Insert the copied column after the original
            this.current_layout.columns.splice(originalIndex + 1, 0, copiedColumn);

            // Update order numbers for all columns
            this.current_layout.columns.forEach((col, i) => {
                col.order_num = i;
            });

            this.current_layout.pending_changes = true;
            this.filtered_columns = [...this.current_layout.columns];
            this.reloadPagination(this.filtered_columns);
            this.$nextTick(() => {
                this.initializeTooltips();
            });
            //Then show the popup
            this.editColumn(copiedColumn);
        },
        initializeModifications() {
            this.current_column.modifications = [
                {
                    from: 0,
                    to: null,
                    action: "no_change",
                    value: "",
                    format_num: false,
                    round_to_nearest: "1",
                    should_round: false
                }
            ];
        },
        handleValueTypeChange(newType) {
            this.current_column.value_type = newType;
            
            // Clear the column value when switching types
            this.current_column.column = "";
            
            // Clear modifications when switching to static
            if (newType === 'static') {
                this.current_column.modifications = [];
            }
            
            // Clear any source errors
            this.errors.source = false;
        },
        changeColumn() {
            this.errors.source = false;
            //If there isn't a name yet, set it to the column name
            if((!this.current_column.export_column_name || this.current_column.export_column_name == "") 
            && this.current_column.column) {
                //Find the text of the column   
                let column 
                if (typeof this.current_column.column === 'object') {
                    column = this.allColumns.find(col => col.value === this.current_column.column.value);
                } else {
                    column = this.allColumns.find(col => col.value === this.current_column.column);
                }
                if (column) {
                    this.current_column.export_column_name = column.text;
                }
            }
            if (!this.current_column.column){ 
                // when clear the input, column got set to null, it breaks the accordion-select, give it a default value as empty string solve the issue
                this.current_column.column = "";
            }
            
            // Initialize modifications if this is a numeric column and modifications don't exist
            if (this.columnIsNumber() && (!this.current_column.modifications || this.current_column.modifications.length === 0)) {
                this.initializeModifications();
            }
        },
        getModificationsTooltip(modifications) {
            if (!modifications || modifications.length === 0) {
                return 'No modifications';
            }
            
            // Handle case where modifications might be a string
            if (typeof modifications === 'string') {
                try {
                    modifications = JSON.parse(modifications);
                } catch (e) {
                    return 'No modifications';
                }
            }
            
            // Filter out no_change modifications
            const activeModifications = modifications.filter(mod => mod.action !== 'no_change');
            
            if (activeModifications.length === 0) {
                return 'No modifications';
            }
            
            let explanations = [];
            
            for (let mod of activeModifications) {
                let fromText = mod.from === 0 ? '$0' : `$${mod.from}`;
                let toText = mod.to === null ? '∞' : `$${mod.to}`;
                let rangeText = `${fromText} to ${toText}`;
                
                let actionText = '';
                if (mod.action === 'multiply') {
                    actionText = `Multiply by ${mod.value}`;
                } else if (mod.action === 'override') {
                    actionText = `Override with $${mod.value}`;
                }
                
                let roundingText = '';
                if (mod.should_round) {
                    roundingText = ` (rounded to nearest $${mod.round_to_nearest})`;
                }
                
                explanations.push(`${rangeText}: <span class="fw-light">${actionText}${roundingText}</span>`);
            }
            
            return explanations.join('<br>');
        },
        getModificationsSummary(modifications) {
            let summary = this.getModificationsTooltip(modifications).replace(/<br>/g, '; ');
            return summary;
        },
        toggleModificationsExpand(colId) {
            this.expandedModifications[colId] = !this.expandedModifications[colId];
        },
        initializeTooltips() {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            for (var i = 0; i < tooltipTriggerList.length; i++) {
                (function(index) {
                    tooltipTriggerList[index].addEventListener('click', function() {
                        tooltipList[index].hide();
                    });
                    tooltipTriggerList[index].addEventListener('mouseleave', function() {
                        tooltipList[index].hide();
                    });
                })(i);
            }
        }
    }
})
</script>

<style scoped>
#column-edit-modal :deep(.card.table-card) {
    --table-radius: 30px;
}
#column-edit-modal :deep(.card.table-card .table thead tr:first-child th:first-child) {
    border-top-left-radius: var(--table-radius);
}
#column-edit-modal :deep(.card.table-card .table thead tr:first-child th:last-child) {
    border-top-right-radius: var(--table-radius);
}
#column-edit-modal :deep(.card.table-card .table tbody tr.last-row td:first-child) {
    border-bottom-left-radius: var(--table-radius);
}
#column-edit-modal :deep(.card.table-card .table tbody tr.last-row td:last-child) {
    border-bottom-right-radius: var(--table-radius);
}
</style>